'use client';

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Bell, X, CheckCheck, Phone, MessageSquare, AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export type NotificationType = 'call' | 'sms' | 'alert' | 'info' | 'success' | 'error' | 'system';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  href?: string;
  meta?: Record<string, unknown>;
}

interface NotificationCenterContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  addNotification: (n: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationCenterContext = createContext<NotificationCenterContextValue>({
  notifications: [],
  unreadCount: 0,
  addNotification: () => {},
  markRead: () => {},
  markAllRead: () => {},
  removeNotification: () => {},
  clearAll: () => {},
});

const STORAGE_KEY = 'lcm_notifications';
const MAX_NOTIFICATIONS = 100;

function loadFromStorage(): AppNotification[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveToStorage(notifications: AppNotification[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications.slice(0, MAX_NOTIFICATIONS)));
  } catch {}
}

export function NotificationCenterProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setNotifications(loadFromStorage());
  }, []);

  useEffect(() => {
    if (mounted) saveToStorage(notifications);
  }, [notifications, mounted]);

  const addNotification = useCallback((n: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => {
    const newN: AppNotification = {
      ...n,
      id: `notif-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      timestamp: new Date().toISOString(),
      read: false,
    };
    setNotifications(prev => [newN, ...prev].slice(0, MAX_NOTIFICATIONS));

    // Browser notification if permitted
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(n.title, { body: n.message, icon: '/assets/images/app_logo.png' });
      } catch {}
    }
  }, []);

  const markRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationCenterContext.Provider value={{
      notifications,
      unreadCount,
      addNotification,
      markRead,
      markAllRead,
      removeNotification,
      clearAll,
    }}>
      {children}
    </NotificationCenterContext.Provider>
  );
}

export function useNotificationCenter() {
  return useContext(NotificationCenterContext);
}

// ─── Notification Center UI Component ────────────────────────────────────────

const TYPE_ICONS: Record<NotificationType, React.ElementType> = {
  call: Phone,
  sms: MessageSquare,
  alert: AlertTriangle,
  info: Info,
  success: CheckCircle,
  error: XCircle,
  system: Bell,
};

const TYPE_COLORS: Record<NotificationType, string> = {
  call: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
  sms: 'text-green-500 bg-green-50 dark:bg-green-900/20',
  alert: 'text-amber-500 bg-amber-50 dark:bg-amber-900/20',
  info: 'text-sky-500 bg-sky-50 dark:bg-sky-900/20',
  success: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
  error: 'text-red-500 bg-red-50 dark:bg-red-900/20',
  system: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20',
};

function formatRelativeTime(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Adesso';
  if (mins < 60) return `${mins}m fa`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h fa`;
  return `${Math.floor(hrs / 24)}g fa`;
}

interface NotificationCenterPanelProps {
  open: boolean;
  onClose: () => void;
}

export function NotificationCenterPanel({ open, onClose }: NotificationCenterPanelProps) {
  const { notifications, unreadCount, markRead, markAllRead, removeNotification, clearAll } = useNotificationCenter();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    setTimeout(() => document.addEventListener('mousedown', handleClick), 100);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  if (!open) return null;

  const filtered = filter === 'unread' ? notifications.filter(n => !n.read) : notifications;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Centro notifiche"
      className="fixed top-14 right-4 z-[9990] w-96 max-h-[80vh] bg-white dark:bg-card border border-border rounded-2xl shadow-modal flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <Bell size={16} className="text-primary" aria-hidden="true" />
          <span className="text-sm font-semibold text-foreground">Notifiche</span>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 bg-primary text-white text-[10px] font-bold rounded-full" aria-label={`${unreadCount} non lette`}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-1 px-2 py-1 text-xs text-primary hover:bg-primary/10 rounded-lg transition-colors"
              aria-label="Segna tutte come lette"
            >
              <CheckCheck size={12} aria-hidden="true" />
              Tutte lette
            </button>
          )}
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground"
            aria-label="Chiudi centro notifiche"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex border-b border-border shrink-0">
        {(['all', 'unread'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${filter === f ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            aria-pressed={filter === f}
          >
            {f === 'all' ? 'Tutte' : `Non lette (${unreadCount})`}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto" role="list" aria-label="Lista notifiche">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <Bell size={32} className="text-muted-foreground/30 mb-3" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">Nessuna notifica</p>
          </div>
        ) : (
          filtered.map(n => {
            const Icon = TYPE_ICONS[n.type];
            const colorClass = TYPE_COLORS[n.type];
            return (
              <div
                key={n.id}
                role="listitem"
                className={`flex items-start gap-3 px-4 py-3 border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer ${!n.read ? 'bg-primary/5' : ''}`}
                onClick={() => markRead(n.id)}
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') markRead(n.id); }}
                aria-label={`${n.title}: ${n.message}${!n.read ? ' (non letta)' : ''}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
                  <Icon size={14} aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-xs font-semibold truncate ${!n.read ? 'text-foreground' : 'text-muted-foreground'}`}>{n.title}</p>
                    <span className="text-[10px] text-muted-foreground shrink-0">{formatRelativeTime(n.timestamp)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); removeNotification(n.id); }}
                  className="w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0 mt-0.5"
                  aria-label={`Rimuovi notifica: ${n.title}`}
                >
                  <X size={10} aria-hidden="true" />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2 border-t border-border shrink-0">
          <button
            onClick={clearAll}
            className="w-full text-xs text-muted-foreground hover:text-destructive transition-colors py-1"
            aria-label="Cancella tutte le notifiche"
          >
            Cancella tutto
          </button>
        </div>
      )}
    </div>
  );
}
